import React, { useState, useEffect } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const WordFamilyTreeGame = ({ gameData, onComplete }) => {
    const [currentWordIndex, setCurrentWordIndex] = useState(0);
    const [feedback, setFeedback] = useState(null);
    const [allWords, setAllWords] = useState([]);

    useEffect(() => {
        if (gameData?.word_families) {
            const words = [];
            gameData.word_families.forEach(family => {
                family.derivatives.forEach(derivative => {
                    words.push({
                        ...derivative,
                        familyId: family.id,
                        familyEmoji: family.emoji
                    });
                });
            });
            setAllWords(words.sort(() => Math.random() - 0.5));
            setCurrentWordIndex(0);
            setFeedback(null);
        }
    }, [gameData]);

    const currentWord = allWords[currentWordIndex];

    const handleChoice = (familyId) => {
        if (!currentWord || feedback) return;
        triggerHaptic(25);

        const isCorrect = currentWord.familyId === familyId;

        if (isCorrect) {
            setFeedback('correct');
            const points = gameData.points_per_word || 15;
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                setFeedback(null);
                if (currentWordIndex + 1 >= allWords.length) {
                    onComplete?.(points);
                } else {
                    setCurrentWordIndex(prev => prev + 1);
                }
            }, 3000);
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => {
                setFeedback(null);
            }, 1500);
        }
    };

    if (!gameData || !currentWord) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-emerald-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">🌳</div>
                <h3 className="text-base md:text-3xl font-black text-emerald-900 tracking-tighter uppercase">Auginamas žodžių medis...</h3>
            </div>
        );
    }

    const progress = (currentWordIndex / allWords.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-emerald-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-emerald-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        🌳
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Žodžių Šeimos Medis
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Žinios</p>
                        <p className="text-sm md:text-2xl font-black text-emerald-600 leading-none">⭐ {currentWordIndex * (gameData.points_per_word || 15)}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Žodis</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentWordIndex + 1}/{allWords.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(16,185,129,0.3)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="flex flex-col items-center justify-center py-4 md:py-12">
            <div className="premium-glass rounded-[2rem] md:rounded-[4rem] p-6 md:p-14 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center w-full max-w-2xl text-center">
                <div className="absolute top-0 right-0 p-4 md:p-8 text-6xl md:text-8xl opacity-[0.03] select-none pointer-events-none">🔍</div>
                <h4 className="text-[10px] font-black text-emerald-400 uppercase tracking-[0.5em] mb-6 md:mb-10">Išvestinis žodis</h4>
                
                <div className="relative group">
                    <div className="absolute -inset-10 bg-emerald-200 rounded-full blur-3xl opacity-20 animate-pulse"></div>
                    <div className="bg-white px-8 py-6 md:px-12 md:py-10 rounded-2xl md:rounded-[3rem] shadow-2xl border-4 border-emerald-100 relative z-10 scale-105 group-hover:scale-110 transition-transform duration-500">
                        <span className="text-4xl md:text-8xl font-black text-emerald-600 leading-none drop-shadow-xl tracking-tighter uppercase">{currentWord.word}</span>
                    </div>
                </div>

                <div className="mt-8 md:mt-14 text-center space-y-4 md:space-y-6">
                    <div className="text-6xl md:text-[140px] drop-shadow-2xl animate-float">{currentWord.familyEmoji}</div>
                    <div className="bg-emerald-50 px-6 py-2 md:px-8 md:py-3 rounded-full md:rounded-[2rem] border-2 border-emerald-100 shadow-inner inline-block">
                        <span className="text-sm md:text-xl font-black text-emerald-700 tracking-widest uppercase italic">{currentWord.type}</span>
                    </div>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-6 w-full">
            {gameData.word_families.map((family) => {
                const isCorrectInFeed = feedback === 'correct' && currentWord.familyId === family.id;

                return (
                    <button
                        key={family.id}
                        onClick={() => handleChoice(family.id)}
                        disabled={!!feedback}
                        className={`
                            premium-button-3d group relative rounded-2xl md:rounded-[3.5rem] p-4 md:p-10 flex flex-col items-center justify-center text-center transition-all duration-500 transform border-b-8 md:border-b-[12px]
                            ${isCorrectInFeed
                                ? 'bg-emerald-500 text-white border-emerald-700 scale-105 shadow-[0_0_50px_rgba(16,185,129,0.5)]'
                                : 'bg-white text-slate-800 border-slate-200 hover:bg-emerald-50 active:translate-y-2 active:border-b-0 shadow-xl'}
                            disabled:cursor-default
                        `}
                    >
                        <div className="text-4xl md:text-7xl mb-2 md:mb-6 group-hover:scale-110 group-hover:rotate-6 transition-transform drop-shadow-2xl">
                            {family.emoji}
                        </div>
                        <h4 className={`text-base md:text-4xl font-black tracking-tighter uppercase leading-none ${isCorrectInFeed ? 'text-white' : 'text-slate-800'}`}>
                            "{family.root}"
                        </h4>
                        <div className={`mt-2 md:mt-6 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl text-[10px] md:text-sm font-bold leading-tight italic font-serif ${isCorrectInFeed ? 'bg-white/20 text-white' : 'bg-slate-50 text-slate-500'}`}>
                            {family.root_meaning}
                        </div>
                        {isCorrectInFeed && (
                            <div className="absolute top-2 right-2 md:top-4 md:right-4 w-8 h-8 md:w-12 md:h-12 bg-white text-emerald-600 rounded-full flex items-center justify-center text-lg md:text-2xl shadow-xl border-2 md:border-4 border-emerald-100 animate-pop-in">✓</div>
                        )}
                    </button>
                );
            })}
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-6 md:p-12 rounded-[2.5rem] md:rounded-[4rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback === 'correct' ? '🌳' : '🍂'}
                        </div>
                        
                        <div className="text-center space-y-4 pt-4">
                            <h2 className={`text-xl md:text-5xl font-black uppercase tracking-tighter ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback === 'correct' ? 'TIKSLI ŠAKNIS!' : 'NE ŠI ŠEIMA...'}
                            </h2>

                            <div className={`p-4 md:p-10 rounded-xl md:rounded-[3rem] border-2 text-left italic font-serif leading-relaxed ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-sm md:text-2xl font-bold text-slate-700 text-center leading-relaxed">
                                    {feedback === 'correct' ? `Nuostabu! Žodis "${currentWord.word}" tikrai kilo iš šaknies "${gameData.word_families.find(f => f.id === currentWord.familyId).root}". Tu puikiai supranti žodžių sandarą!` : `Oi! Panašu, kad šis žodis auga ant kito medžio. Pabandyk dar kartą pagalvoti apie žodžio kilmę!`}
                                </p>
                            </div>

                            <div className="flex items-center justify-center gap-4">
                                <div className="flex gap-2">
                                    <div className="w-2 h-2 md:w-3 md:h-3 bg-slate-200 rounded-full animate-bounce" />
                                    <div className="w-2 h-2 md:w-3 md:h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]" />
                                    <div className="w-2 h-2 md:w-3 md:h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]" />
                                </div>
                                <span className="text-[10px] md:text-sm font-black text-slate-300 uppercase tracking-[0.3em]">
                                    {feedback === 'correct' ? 'Kitas žodis...' : 'Taisome klaidą...'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default WordFamilyTreeGame;